import { EColumnType } from '@/editor/data/model/model';
import { RulerModel } from '@/editor/entities/2d/rulerModel';
import { IPoint2d } from '@/editor/math/IPoint2d';
import { Point2dUtils } from '@/editor/math/Point2dUtils';
import { globalStores } from '@/editor/stores';
import { calculateBoxRulerInfos, IBoxRulerInfo } from '@/editor/stores/utils/ruler/model';
import { getAllStructOutlines } from '@/editor/stores/utils/column';
import { EEntityType } from '@/editor/types';
import { getBoxRayDetectOutlines } from '@/editor/stores/utils/ruler/outlines';
import { isMultiSelectFurnitureOrAssemblyOnly } from '@/editor/utils/select/multi-select';
import { modelManager } from '@/editor/manager/entityManager';

interface IEditableBoxRulerInfo extends IBoxRulerInfo {
  editable: boolean;
}

type TAllowedEntityType = EEntityType.Column | EEntityType.Furniture | EEntityType.Assembly | EEntityType.MultiModel;

export class BoxRulerController {
  private _mainEntityId: string = '';
  private _entityType: TAllowedEntityType | undefined;
  private _rulerModel = new RulerModel<IEditableBoxRulerInfo>({
    onCommit: this.rulerOnChange.bind(this),
    onChange: this.rulerOnChange.bind(this)
  });

  private _rayTestedOutlines: { start: IPoint2d; end: IPoint2d }[] = [];

  private _disposeListener = () => {};

  constructor(entityId: string, entityType: TAllowedEntityType) {
    this._mainEntityId = entityId;
    this._entityType = entityType;

    const disposeMovingObserve = globalStores.editorStateStore.onChange('moving', (isMoving) => {
      if (isMoving) {
        this._rulerModel.clear();
      } else {
        this.update();
      }
    });
    this._disposeListener = () => {
      disposeMovingObserve();
    };

    switch (entityType) {
      case EEntityType.Column:
        this._rayTestedOutlines = getAllStructOutlines(entityId);
        break;
      case EEntityType.Furniture:
        // 家具射线需要检测家具和柱子轮廓线
        this._rayTestedOutlines = getBoxRayDetectOutlines([entityId]);
        break;
      case EEntityType.Assembly:
        this._rayTestedOutlines = getBoxRayDetectOutlines(
          globalStores.getDataByIdType(entityId, entityType)?.accessorieIds
        );
        break;
      case EEntityType.MultiModel: {
        const multiModelFurnitureId = globalStores
          .getDataByIdType('', EEntityType.MultiModel)
          ?.modelInfos.reduce<string[]>((acc, cur) => {
            switch (cur.type) {
              case EEntityType.Furniture: {
                acc.push(cur.id);
                break;
              }
              case EEntityType.Assembly: {
                acc.push(...cur.subModeldatas.map((item) => item.id));
                break;
              }
            }
            return acc;
          }, []);
        this._rayTestedOutlines = getBoxRayDetectOutlines(multiModelFurnitureId);
        break;
      }
    }
  }

  private getEntityBoxData() {
    switch (this._entityType) {
      case EEntityType.Assembly:
        return globalStores.assemblyStore.getAssemblyData(this._mainEntityId);
      case EEntityType.MultiModel: {
        const multiModelData = globalStores.getDataByIdType('', EEntityType.MultiModel);
        if (isMultiSelectFurnitureOrAssemblyOnly(multiModelData)) {
          return multiModelData;
        }
        return null;
      }
      case EEntityType.Column: {
        const columnData = globalStores.structStore.getColumnData(this._mainEntityId);
        if (!columnData) return null;
        return {
          center: columnData.center,
          width: columnData.type === EColumnType.squareColumn ? columnData.width : columnData.radius * 2,
          thickness: columnData.type === EColumnType.squareColumn ? columnData.thickness : columnData.radius * 2,
          rotation: columnData.rotation
        };
      }
      case EEntityType.Furniture:
        return globalStores.modelStore.getFurnitureData(this._mainEntityId);
      default:
        return null;
    }
  }

  // 标尺处理
  update() {
    if (!this._mainEntityId) return;
    const entityData = this.getEntityBoxData();

    if (!entityData) return;

    const rulerInfos: IEditableBoxRulerInfo[] = calculateBoxRulerInfos(
      entityData,
      this._rayTestedOutlines,
      this._entityType === EEntityType.Column
    ).map((info) => ({ ...info, editable: true, focusTrap: true, blurOnCommit: true }));

    this._rulerModel.updateModel(rulerInfos);
    modelManager.selectedRerender();
  }

  private rulerOnChange(id: string, value: number, rulerInfo: IEditableBoxRulerInfo) {
    const entityData = this.getEntityBoxData();
    if (!entityData) return;

    const oldValue = Point2dUtils.distanceTo(rulerInfo.start, rulerInfo.end);
    const diffDistance = value - oldValue;
    const newCenter = Point2dUtils.addDistance(entityData.center, -diffDistance, rulerInfo.rayDirection);

    switch (this._entityType) {
      case EEntityType.Column:
        globalStores.structOperation.modifyColumn(this._mainEntityId, { center: newCenter });
        break;
      case EEntityType.Furniture:
        globalStores.furnitureOperation.modifyFurniture(this._mainEntityId, { center: newCenter });
        break;
      case EEntityType.Assembly:
        globalStores.assemblyOperation.moveAssembly(this._mainEntityId, newCenter);
        break;
      case EEntityType.MultiModel: {
        const multiModelData = globalStores.getDataByIdType('', EEntityType.MultiModel);
        if (isMultiSelectFurnitureOrAssemblyOnly(multiModelData)) {
          const offset = Point2dUtils.subtracted(newCenter, multiModelData.center);
          globalStores.multiModelOperation.moveMultiModel(offset, multiModelData.modelInfos);
        }
        break;
      }
    }
    this.update();
  }

  dispose() {
    this._disposeListener();
    this._rulerModel.dispose();
  }
}
